import React, { useState } from 'react';
import { Modal, Box, Typography, TextField, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DoorIcon from '../../../_icons/DoorIcon';

// ============================================
// Styled Components
// ============================================
const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '392px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  overflow: 'hidden',
  outline: 'none',
});

const ModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '48px',
  padding: '0 24px',
  backgroundColor: '#262626',
});

const ModalTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
  whiteSpace: 'nowrap',
});

const CloseButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  '& svg': {
    color: '#fff',
    fontSize: '24px',
  },
});

const ModalContent = styled(Box)({
  display: 'flex',
  gap: '24px',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  backgroundColor: '#fafbfc',
});

const OptionCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  width: '160px',
  height: '140px',
  padding: '12px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.1)',
  cursor: 'pointer',
  border: isSelected ? '2px solid #4ab3bc' : '2px solid transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    border: '2px solid #4ab3bc',
  },
}));

const OptionDescription = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#737373',
  textAlign: 'center',
  lineHeight: 1.4,
});

const OptionIconWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
});

const OptionLabel = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#4ab3bc',
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

const ModalBody = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '24px',
  backgroundColor: '#fafbfc',
});

const TimeRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const TimeLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#4D4D4D',
  whiteSpace: 'nowrap',
});

const TimeHint = styled(Typography)({
  fontSize: '12px',
  color: '#999',
});

const ConfirmButton = styled(Button)({
  height: '44px',
  borderRadius: '8px',
  backgroundColor: '#4ab3bc',
  color: '#fff',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: '#3a9aa2' },
  '&.Mui-disabled': { backgroundColor: '#B8DADB', color: '#fff' },
});

// ============================================
// Main Component — 항시열림 방식 선택: 계속 열기(always) / 지정 시각까지(time, date=HH:mm)
// ============================================
const DoorSettingModal = ({ open, onClose, onConfirm, isPending = false }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState('always'); // 'always' | 'time'
  const [until, setUntil] = useState('');

  const canConfirm = mode === 'always' || /^\d{2}:\d{2}$/.test(until);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{t('항시열림')}</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          <OptionCard isSelected={mode === 'always'} onClick={() => setMode('always')}>
            <OptionDescription>{t('자동잠김을 누를 때까지 열립니다')}</OptionDescription>
            <OptionIconWrapper>
              <DoorIcon width={40} height={40} color="#4ab3bc" />
              <OptionLabel>{t('계속 열기')}</OptionLabel>
            </OptionIconWrapper>
          </OptionCard>
          <OptionCard isSelected={mode === 'time'} onClick={() => setMode('time')}>
            <OptionDescription>{t('지정한 시각에 자동으로 잠깁니다')}</OptionDescription>
            <OptionIconWrapper>
              <AccessTimeIcon style={{ fontSize: 40, color: '#4ab3bc' }} />
              <OptionLabel>{t('지정 시각까지')}</OptionLabel>
            </OptionIconWrapper>
          </OptionCard>
        </ModalContent>
        <ModalBody>
          {mode === 'time' && (
            <>
              <TimeRow>
                <TimeLabel>{t('잠기는 시각')}</TimeLabel>
                <TextField
                  type="time"
                  size="small"
                  value={until}
                  onChange={e => setUntil(e.target.value)}
                  inputProps={{ step: 60 }}
                  sx={{ flex: 1, '& input': { textAlign: 'center' } }}
                />
              </TimeRow>
              <TimeHint>{t('이미 지난 시각이면 다음 날 그 시각에 잠깁니다.')}</TimeHint>
            </>
          )}
          <ConfirmButton disabled={!canConfirm || isPending} onClick={() => onConfirm({ mode, until })}>
            {isPending ? t('전환 중...') : mode === 'time' && until ? `${until}${t('까지 열기')}` : t('열기')}
          </ConfirmButton>
        </ModalBody>
      </ModalContainer>
    </Modal>
  );
};

export default DoorSettingModal;
